/**
 * Petty Cash Purchases — what the sites bought with cash from the float.
 *
 * A store-man records a small local purchase as an ad-hoc goods receipt marked
 * "paid from site petty cash". The system already turns that into a real cash
 * payment against a real payable, so the money is properly recorded.
 *
 * What was missing was somewhere to LOOK at them. Answering "how much cash has
 * Khubal got through this month, and on what?" meant opening receipts one at a
 * time. This is that list.
 *
 * READ-ONLY, FOR EVERYONE WHO CAN SEE IT
 *
 * Nothing here creates or changes a payment. The purchase is made at site and
 * recorded on the goods receipt; this only reports it. Anyone wanting to change
 * something goes to the receipt.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, Banknote, Download, Paperclip, Store, Wallet,
} from 'lucide-react'
import {
  Alert, Card, EmptyState, Field, Input, Loading, SearchInput, Select, Stat,
  Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { NotPermitted } from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { useQuery } from '@/lib/data'
import { downloadFromFunction, supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees, localIso } from '@/lib/format'
import { can } from '@/lib/domain'

type Row = {
  payment_id: string
  payment_date: string
  posting_date: string | null
  posting_month: string | null
  amount: number
  reference_no: string | null
  grn_id: string
  received_date: string
  site_id: string
  site_name: string | null
  pm_name: string | null
  challan_no: string | null
  entered_by: string | null
  approved_by: string | null
  vendor_id: string | null
  vendor_name: string | null
  vendor_gstin: string | null
  vendor_unidentified: boolean
  taxable_amount: number | null
  gst_amount: number | null
  materials: string | null
  attachments: number
}

type SiteRow = {
  site_id: string
  site_name: string
  pm_name: string | null
  purchases: number
  shops: number
  total_spent: number
  spent_this_month: number
  still_unidentified: number
  last_purchase: string | null
}

const TODAY = new Date()
const START_OF_FY = new Date(
  TODAY.getMonth() >= 3 ? TODAY.getFullYear() : TODAY.getFullYear() - 1, 3, 1,
)
const iso = (d: Date) => localIso(d)

export default function PettyCash() {
  const { profile } = useAuth()
  const toast = useToast()
  const mayUse = can(profile?.role, 'viewPettyCash')

  const [from, setFrom] = useState(iso(START_OF_FY))
  const [to, setTo] = useState(iso(TODAY))
  const [site, setSite] = useState('')
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState(false)
  const { sort, toggle } = useSort('payment_date', 'desc')

  /**
   * The site filter, narrowed to a Project Manager's own sites.
   *
   * `sites` is a master and is readable by everybody — deliberately, it is
   * just names. Row level security already stops him seeing another site's
   * cash, so leaving the list whole would not LEAK anything. It would simply
   * offer him eighteen choices, fifteen of which return an empty table with no
   * explanation, and an empty table looks like a broken screen long before it
   * looks like a permission.
   *
   * So the filter lists what he can actually ask about. The control is the
   * policy; this is the courtesy.
   */
  const { data: sites } = useQuery<{ id: string; site_name: string }>(
    'sites',
    () => {
      const q = supabase.from('sites').select('id, site_name').order('site_name')
      return profile?.role === 'project_manager' && profile?.id
        ? q.eq('project_manager_id', profile.id)
        : q
    },
    [profile?.role, profile?.id])

  const { data: rows, loading } = useQuery<Row>(
    'v_petty_cash_purchases',
    () => {
      let q = supabase.from('v_petty_cash_purchases').select('*')
        .gte('payment_date', from).lte('payment_date', to)
      if (site) q = q.eq('site_id', site)
      return q.order('payment_date', { ascending: false }).limit(2000)
    },
    [from, to, site],
    { cacheable: false, idField: 'payment_id' },
  )

  const { data: bySite } = useQuery<SiteRow>(
    'v_petty_cash_by_site', () => supabase.from('v_petty_cash_by_site').select('*'), [],
    { cacheable: false, idField: 'site_id' })

  const visible = useMemo(() => {
    const t = search.trim().toLowerCase()
    if (!t) return rows
    return rows.filter((r) => [r.grn_id, r.vendor_name, r.site_name, r.challan_no,
      r.materials, r.entered_by].some((f) => (f ?? '').toLowerCase().includes(t)))
  }, [rows, search])

  const sorted = useMemo(() => sortRows(visible, sort, {
    payment_date: 'date', received_date: 'date', amount: 'number',
  }), [visible, sort])

  const totals = useMemo(() => ({
    count: visible.length,
    spent: visible.reduce((a, r) => a + Number(r.amount || 0), 0),
    unnamed: visible.filter((r) => r.vendor_unidentified).length,
    sites: new Set(visible.map((r) => r.site_id)).size,
  }), [visible])

  const company = useMemo(() => ({
    month: bySite.reduce((a, s) => a + Number(s.spent_this_month || 0), 0),
    all: bySite.reduce((a, s) => a + Number(s.total_spent || 0), 0),
    unnamed: bySite.reduce((a, s) => a + Number(s.still_unidentified || 0), 0),
  }), [bySite])

  if (!mayUse) {
    return <NotPermitted what="the petty cash register"
                         who="Accounts, the Head of Stores, the Procurement Manager, the Procurement Executives and the Project Head" />
  }

  async function exportIt() {
    if (busy) return
    setBusy(true)
    try {
      await downloadFromFunction('export-report',
        { report: 'petty_cash_register', filters: site ? { site_id: site } : {} },
        'petty_cash_register.xlsx')
      toast.success('Workbook downloaded.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not export the register.')
    } finally { setBusy(false) }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Petty Cash Purchases</h1>
        <p className="text-sm text-slate-500">
          What the sites bought with cash from the float, and what it cost.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Spent this month" value={rupees(company.month)}
              sub="company-wide, all sites" icon={<Banknote className="h-5 w-5" />} />
        <Stat label="Spent in total" value={rupees(company.all)}
              sub="every petty cash purchase on file" icon={<Wallet className="h-5 w-5" />} />
        <Stat label="Shops not yet named" value={String(company.unnamed)}
              sub={company.unnamed > 0 ? 'sitting against an OTHERS placeholder' : 'every shop identified'}
              tone={company.unnamed > 0 ? 'warn' : 'good'}
              icon={<Store className="h-5 w-5" />} />
        <Stat label="Shown below" value={String(totals.count)}
              sub={`${rupees(totals.spent)} across ${totals.sites} site(s)`}
              icon={<Paperclip className="h-5 w-5" />} />
      </div>

      <Alert tone="info" title="Where these come from">
        Each row is a real cash payment, created when the Head of Stores approved an
        ad-hoc goods receipt marked <strong>paid from site petty cash</strong>. Nothing on
        this screen creates or changes anything — the purchase is recorded on the receipt,
        and this only reports it.
      </Alert>

      {bySite.length > 0 && (
        <Card title="By site" subtitle="Where the cash is going, and how recently">
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Project Manager</th>
                  <th className="text-right">Purchases</th>
                  <th className="text-right">Shops</th>
                  <th className="text-right">This month</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">Shop not named</th>
                  <th>Last purchase</th>
                </tr>
              </thead>
              <tbody>
                {bySite.map((s) => (
                  <tr key={s.site_id} className="hover:bg-oval-50">
                    <td>
                      <button className="font-semibold text-oval-700 hover:underline"
                              onClick={() => setSite(s.site_id)}>
                        {s.site_name}
                      </button>
                    </td>
                    <td className="text-slate-600">{s.pm_name ?? '—'}</td>
                    <td className="text-right tabular-nums">{s.purchases}</td>
                    <td className="text-right tabular-nums">{s.shops}</td>
                    <td className="text-right tabular-nums">{rupees(s.spent_this_month)}</td>
                    <td className="text-right font-semibold tabular-nums">{rupees(s.total_spent)}</td>
                    <td className={`text-right tabular-nums ${
                      s.still_unidentified > 0 ? 'font-semibold text-amber-700' : 'text-slate-400'}`}>
                      {s.still_unidentified > 0 ? s.still_unidentified : '—'}
                    </td>
                    <td className="whitespace-nowrap text-slate-600">
                      {s.last_purchase ? ddmmyyyy(s.last_purchase) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="Every purchase"
            actions={
              <button className="btn-secondary btn-sm" onClick={() => void exportIt()} disabled={busy}>
                <Download className="mr-1 h-4 w-4" /> {busy ? 'Preparing…' : 'Export'}
              </button>
            }>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Paid from">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="to">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label="Site">
            <Select value={site} onChange={(e) => setSite(e.target.value)}
                    placeholder="Every site"
                    options={sites.map((s) => ({ value: s.id, label: s.site_name }))} />
          </Field>
          <Field label="Search">
            <SearchInput value={search} onChange={setSearch}
                         placeholder="Shop, material, receipt…" />
          </Field>
        </div>

        {loading && <Loading label="Loading petty cash purchases…" />}

        {!loading && sorted.length === 0 && (
          <EmptyState icon={<Banknote className="h-8 w-8" />}
                      title="Nothing here"
                      message="No petty cash purchase in this period." />
        )}

        {!loading && sorted.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="payment_date" sort={sort} onSort={toggle}>Paid on</Th>
                  <Th id="site_name" sort={sort} onSort={toggle}>Site</Th>
                  <Th id="vendor_name" sort={sort} onSort={toggle}>Shop</Th>
                  <th>What it bought</th>
                  <Th id="grn_id" sort={sort} onSort={toggle}>Receipt</Th>
                  <th>Bill</th>
                  <Th id="amount" sort={sort} onSort={toggle} align="right">Cash paid</Th>
                  <th>Entered by</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.payment_id} className="hover:bg-oval-50">
                    <td className="whitespace-nowrap">
                      {ddmmyyyy(r.payment_date)}
                      {r.posting_month && r.posting_date !== r.payment_date && (
                        <div className="text-[11px] text-slate-400">posts {r.posting_month}</div>
                      )}
                    </td>
                    <td>{r.site_name ?? r.site_id}</td>
                    <td>
                      {r.vendor_unidentified ? (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <AlertTriangle className="h-3.5 w-3.5" /> Not named yet
                        </span>
                      ) : (
                        <>
                          {r.vendor_name}
                          {r.vendor_gstin && (
                            <div className="font-mono text-[11px] text-slate-400">{r.vendor_gstin}</div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="max-w-[280px] text-xs text-slate-600">
                      {r.materials ?? '—'}
                    </td>
                    <td className="whitespace-nowrap">
                      <Link to={`/grns/${r.grn_id}`}
                            className="font-mono text-xs font-semibold text-oval-700 hover:underline">
                        {r.grn_id}
                      </Link>
                      <div className="text-[11px] text-slate-400">
                        received {ddmmyyyy(r.received_date)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap text-xs">
                      {r.challan_no ?? '—'}
                      {r.attachments > 0 && (
                        <div className="text-[11px] text-slate-400">{r.attachments} file(s)</div>
                      )}
                    </td>
                    <td className="text-right">
                      <div className="font-semibold tabular-nums">{rupees(r.amount)}</div>
                      {Number(r.gst_amount) > 0 && (
                        <div className="text-[11px] text-slate-400">
                          incl. GST {rupees(r.gst_amount)}
                        </div>
                      )}
                    </td>
                    <td className="text-xs text-slate-500">{r.entered_by ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {company.unnamed > 0 && (
        <Alert tone="warning" title={`${company.unnamed} shop(s) still sitting against a placeholder`}>
          A purchase shown as <strong>Not named yet</strong> was bought from a shop nobody
          has added to the vendor master. Open the goods receipt, read the attached bill,
          and press <strong>Name the vendor</strong>. Until then the input tax credit on it
          cannot be claimed.
        </Alert>
      )}
    </div>
  )
}
